import {state} from '@bitstillery/mithril'
import {$t} from '@bitstillery/common/app'
import {Registry} from '@bitstillery/common/lib/store'
import {CartValidation} from '@bitstillery/common/api/fact2server'
import {format_money_with_symbol} from '@bitstillery/common/lib/format'
import {present} from '@bitstillery/common/lib/utils'

import type {State} from '@bitstillery/mithril'

import type {CartItemLookup, CartItemState, CartOfferItem} from '@/models/cart'
import {unit_price} from '@/models/offer'
import {$s} from '@/app'

export const DEFAULT_MAX_QUANTITY = 99_999

/** The icon, tip and look of a stepper button for the quantity as it stands. */
export interface QuantityButtonState {
    icon: string
    tip: string
    type: 'default' | 'info' | 'success' | 'warning'
}

/**
 * The quantity context's state as its readers see it: each computed reads as its value. `_stock_units` is
 * null for an offer item without a stock limit, which no comparison may read as 0 (no stock).
 * `_model_value` is null while the input is empty, which the comparisons count as 0 (`?? 0`): an emptied
 * input asks to remove the item.
 */
export interface QuantityData {
    _cart_cases: number
    _cart_invalid: boolean
    _cart_item: State<CartItemState> | undefined
    _cart_units: number
    _less_stock_available: boolean
    _min_quantity_active: boolean
    _min_quantity_cases: number
    _min_quantity_invalid_cart: boolean
    _min_quantity_invalid_modelvalue: boolean
    _min_quantity_unit: number
    _model_value: number | null
    _model_value_cases: number
    _model_value_invalid: boolean
    _model_value_override: number | null | undefined
    _model_value_unit: number | null
    _out_of_stock: boolean
    _price_invalid_cart: boolean
    _quantity_invalid: boolean
    _quantity_invalid_cart: boolean
    _quantity_invalid_modelvalue: boolean
    _step_size_quantity_active: boolean
    _step_size_quantity_invalid_cart: boolean
    _step_size_quantity_invalid_modelvalue: boolean
    _step_size_unit: number
    _stock_units: number | null
    left: {
        _active: boolean
        _disabled: boolean
        /** Null without cart items, when the left button isn't in use. */
        _state: QuantityButtonState | null | undefined
    }
    loading: boolean
    right: {
        _active: boolean
        _disabled: boolean | undefined
        _state: QuantityButtonState | undefined
    }
}
/**
 * Creates a quantity context for managing product quantity state and validation.
 *
 * The context includes:
 * - Reactive proxy with all computed quantity properties
 * - Helper methods for finding valid quantities (MOQ, step_size, stock constraints)
 */
export function create_quantity_context(offer_item: CartOfferItem) {
    // Create the reactive data proxy with all computed properties
    const data: QuantityData = state({
        _cart_cases: () => {
            const cart_item = data._cart_item
            if (cart_item) {
                return cart_item.number_of_cases
            }
            return 0
        },

        _cart_invalid: () =>
            data._min_quantity_invalid_cart || data._step_size_quantity_invalid_cart || data._less_stock_available,

        _cart_item: () => {
            const cart_items: CartItemLookup = $s.cart.items
            const item = cart_items[offer_item.case_artkey]
            // Treat items with 0 cases or marked as invisible as non-existent
            // This ensures UI updates correctly when item is being deleted
            if (item && (item.number_of_cases === 0 || item._invisible)) {
                return
            }
            return item
        },

        _cart_units: () => {
            if ($s.cart.show_bottles) {
                return data._cart_cases * offer_item.case_number_of_bottles
            }
            return data._cart_cases
        },

        _less_stock_available: () => {
            const cart_item = data._cart_item
            if (!cart_item) {
                return false
            }
            if (data._out_of_stock) {
                return false
            }
            return cart_item.case_artkey in $s.cart.errors[CartValidation.QUANTITY]
        },

        _min_quantity_active: () => data._min_quantity_cases > 1,

        _min_quantity_cases: () => {
            const min_quantity_cases = offer_item.minimum_quantity ?? 1
            if (offer_item.list_quantity !== null && offer_item.list_quantity < min_quantity_cases) {
                return offer_item.list_quantity
            }
            return min_quantity_cases
        },

        _min_quantity_unit: () => {
            const cart_item = data._cart_item
            let min_quantity_cases = data._min_quantity_cases

            const min_quantity_error = cart_item ? $s.cart.errors[CartValidation.MIN_QUANTITY][cart_item.case_artkey] : undefined
            if (min_quantity_error) {
                min_quantity_cases = min_quantity_error.valid_value
            }

            if ($s.cart.show_bottles) {
                return min_quantity_cases * offer_item.case_number_of_bottles
            }
            return min_quantity_cases
        },

        _min_quantity_invalid_cart: () => {
            if (!data._cart_units) {
                return false
            }

            if (data._cart_units === data._stock_units) {
                // Buying remaining items is always valid.
                return false
            }

            return data._cart_units < data._min_quantity_unit
        },

        _min_quantity_invalid_modelvalue: () => {
            if (!data._model_value) {
                return false
            }
            return data._model_value < data._min_quantity_unit
        },

        // Local override for input editing - when undefined, _model_value falls back to cart_item.value or _cart_units.
        // Use undefined (not null) for "no override" so that null can mean "user explicitly cleared to empty"
        // (which shows the trash icon for removing the item from cart).
        _model_value_override: undefined,

        // Get/set descriptor: getter returns computed value, setter updates override
        // This allows FieldNumber to bind to _model_value directly for both display and editing
        _model_value: {
            get: (): number | null => {
                if (data._model_value_override !== undefined) {
                    return data._model_value_override
                }
                // Fall back to _cart_units which is computed from cart_item.number_of_cases
                // Don't use cart_item.value as it may be stale after cart updates
                return data._cart_units || null
            },
            set: (value: number | null) => {
                data._model_value_override = value
            },
        },

        _model_value_cases: () => {
            const model_value = data._model_value ?? 0
            if ($s.cart.show_bottles) {
                return Math.ceil(model_value / offer_item.case_number_of_bottles)
            }
            return model_value
        },

        _model_value_invalid: () => {
            if (data._model_value === null) {
                return false
            }
            return (
                (data._stock_units !== null && data._model_value > data._stock_units) ||
                data._min_quantity_invalid_modelvalue ||
                data._step_size_quantity_invalid_modelvalue
            )
        },

        _model_value_unit: () => {
            const step_size_cases = offer_item.step_size_quantity
            if (step_size_cases === null) {
                return null
            }
            if ($s.cart.show_bottles) {
                const cart_item = data._cart_item
                if (cart_item) {
                    return cart_item.case_number_of_bottles * step_size_cases
                }
                return offer_item.case_number_of_bottles * step_size_cases
            }
            return step_size_cases
        },

        _out_of_stock: () => {
            const cart_item = data._cart_item
            if (cart_item) {
                return $s.cart.errors[CartValidation.QUANTITY][cart_item.case_artkey]?.valid_value === 0
            }
            return data._stock_units === 0
        },

        _price_invalid_cart: () => {
            if (!data._cart_units) {
                return false
            }
            const cart_item = data._cart_item
            return Boolean(cart_item && cart_item.case_artkey in $s.cart.errors[CartValidation.PRICE_WAS_RAISED])
        },

        _quantity_invalid: () => data._quantity_invalid_modelvalue || data._quantity_invalid_cart,

        _quantity_invalid_modelvalue: () => {
            if (data._model_value === data._stock_units || data._stock_units === null) {
                // Intend buying remaining items and tbo without max quantity are always valid.
                return false
            }

            return (
                (data._model_value ?? 0) > data._stock_units ||
                data._min_quantity_invalid_modelvalue ||
                data._step_size_quantity_invalid_modelvalue
            )
        },

        _quantity_invalid_cart: () => {
            if (!data._cart_units) {
                return false
            }

            if (data._cart_units === data._stock_units) {
                // Buying remaining items is always valid.
                return false
            }

            return data._min_quantity_invalid_cart || data._step_size_quantity_invalid_cart
        },

        _step_size_quantity_active: () => data._step_size_unit > 1,

        _step_size_quantity_invalid_cart: () => {
            if (!data._cart_units || data._cart_units === data._min_quantity_unit) {
                return false
            }

            return data._cart_units % data._step_size_unit !== 0
        },

        _step_size_quantity_invalid_modelvalue: () => {
            if (!data._model_value || data._model_value === data._min_quantity_unit) {
                return false
            }

            if (data._model_value === data._stock_units) {
                // Buying the remaining stock is always valid.
                return false
            }

            return data._model_value % data._step_size_unit !== 0
        },

        _step_size_unit: () => {
            const step_size = offer_item.step_size_quantity ?? 1
            if ($s.cart.show_bottles) {
                return offer_item.case_number_of_bottles * step_size
            }
            return step_size
        },

        _stock_units: () => {
            const cart_item = data._cart_item
            let list_quantity_cases = offer_item.list_quantity

            // Use validated quantity from backend if available
            const quantity_error = cart_item ? $s.cart.errors[CartValidation.QUANTITY][cart_item.case_artkey] : undefined
            if (quantity_error) {
                list_quantity_cases = quantity_error.valid_value
            }

            if ($s.cart.show_bottles && list_quantity_cases !== null) {
                return list_quantity_cases * offer_item.case_number_of_bottles
            }

            return list_quantity_cases
        },

        left: {
            _active: () => data._cart_units > 0,
            _disabled: () => {
                // Price error validation is always the same; right button is a confirmation, left button is a remove.
                if (data._price_invalid_cart) {
                    return false
                }

                if (data._quantity_invalid_modelvalue) {
                    return true
                }

                if ((data._model_value ?? 0) < data._cart_units) {
                    // The input value of FieldNumber is used as the next value; check if it's
                    // Valid against MOQ, step size, and stock.
                    return (
                        (data._stock_units !== null && (data._model_value ?? 0) > data._stock_units) ||
                        data._step_size_quantity_invalid_modelvalue ||
                        data._min_quantity_invalid_modelvalue
                    )
                } else if (data._model_value === data._cart_units) {
                    // There is a previous valid quantity; allow the minus button.
                    return context.find_prev_valid_units(data._cart_units) >= data._cart_units
                } else if ((data._model_value ?? 0) > data._cart_units) {
                    // The minus button is disabled when the FieldNumber input amount suggests adding items.
                    return true
                }
                return true
            },
            _state: (): QuantityButtonState | null | undefined => {
                // The left button is not in use, without cart items.
                if (!data._cart_units) {
                    return null
                }

                if (data._price_invalid_cart) {
                    return {
                        icon: 'trash',
                        tip: $t('cart.actions.minus_all', {product: offer_item.product_name}),
                        type: 'default',
                    }
                }

                if (!data._model_value || data._model_value === 0) {
                    // Extra case; removing the number in FieldNumber means intend to remove.
                    return {
                        icon: 'trash',
                        tip: $t('cart.actions.minus_all', {product: offer_item.product_name}),
                        type: 'warning',
                    }
                } else if (data._model_value > data._cart_units) {
                    // Intended amount is greater than the cart units, so the next left action is disabled.
                    return {
                        icon: 'minus',
                        tip: $t('cart.actions.minus_disabled', {product: offer_item.product_name}),
                        type: 'default',
                    }
                } else if (data._model_value === data._cart_units) {
                    // The next left action is to confirm removal on the left edge.
                    if (data._model_value === data._min_quantity_unit) {
                        return {
                            icon: 'trash',
                            tip: $t('cart.actions.minus_all', {product: offer_item.product_name}),
                            type: 'warning',
                        }
                    }
                    let tip
                    const prev_valid_quantity = context.find_prev_valid_units(data._model_value)
                    if (prev_valid_quantity < data._cart_units) {
                        tip = $t('cart.actions.minus', {
                            count: data._cart_units - prev_valid_quantity,
                            product: offer_item.product_name,
                            units: $t(`unit.${$s.identity.user.price_preference}`, {
                                count: data._cart_units - prev_valid_quantity,
                            }),
                        })
                    } else {
                        tip = $t('cart.actions.minus_disabled', {product: offer_item.product_name})
                    }
                    return {
                        icon: 'minus',
                        tip,
                        type: 'default',
                    }
                } else if (data._model_value < data._cart_units) {
                    let icon = 'checked'
                    let tip
                    let type: QuantityButtonState['type'] = data._cart_invalid ? 'warning' : 'default'

                    if (data._model_value < data._min_quantity_unit) {
                        // Everything beneath min quantity is just invalid; it is caused
                        // By invalid FieldNumber input.
                        icon = 'minus'
                        tip = $t('cart.actions.minus_disabled', {product: offer_item.product_name})
                    } else {
                        if (data._model_value_invalid) {
                            tip = $t('cart.actions.minus_disabled', {product: offer_item.product_name})
                        } else {
                            type = data._cart_invalid ? 'warning' : 'success'
                            tip = $t('cart.actions.minus', {
                                count: data._cart_units - data._model_value,
                                product: offer_item.product_name,
                                units: $t(`unit.${$s.identity.user.price_preference}`, {
                                    count: data._cart_units - data._model_value,
                                }),
                            })
                        }
                    }

                    return {icon, tip, type}
                }
                return undefined
            },
        },

        loading: false,

        right: {
            _active: () => data._stock_units === null || data._stock_units > 0,
            _disabled: () => {
                // Always activate if Price validation is active; the confirm button
                // Then concerns fixing the price only.
                if (data._price_invalid_cart) {
                    return false
                }

                // If the stock is explicitly null, there is no stock limit and there shouldn't be any quantity issues.
                // Always allow adding.
                if (data._stock_units === null) {
                    let max_quantity = DEFAULT_MAX_QUANTITY
                    if ($s.cart.show_bottles) {
                        max_quantity *= offer_item.case_number_of_bottles
                    }
                    return data._cart_units >= max_quantity
                }

                if ((data._model_value ?? 0) < data._cart_units) {
                    // The plus button is disabled when the FieldNumber input amount suggests removing items.
                    return true
                } else if (data._model_value === data._cart_units) {
                    // First check if the current quantity is valid against MOQ, step size, and stock.
                    if (data._quantity_invalid_modelvalue) {
                        return true
                    }

                    // FieldNumber is in sync with the cart; find out next valid quantity. If it is higher
                    const next_valid_quantity = context.find_next_valid_units(data._cart_units)
                    return next_valid_quantity <= data._cart_units
                } else if ((data._model_value ?? 0) > data._cart_units) {
                    // The input value of FieldNumber is used as the next value; check if it's
                    // Valid against MOQ, step size, and stock.
                    return data._quantity_invalid_modelvalue
                }
                return undefined
            },
            _state: (): QuantityButtonState | undefined => {
                // Not in cart yet; this is a plain add button.
                if (!data._cart_units) {
                    return {
                        icon: 'cartPlus',
                        tip: $t('cart.actions.plus_all', {product: offer_item.product_name}),
                        // Add-to-cart is the product's primary action, not a status signal; info keeps
                        // the stepper in the brand family (solid chrome toggle) instead of vivid green.
                        type: 'info',
                    }
                }

                // Price invalid always uses the same format; left is delete, right is confirm.
                if (data._price_invalid_cart) {
                    const price_error = present(
                        $s.cart.errors[CartValidation.PRICE_WAS_RAISED][offer_item.case_artkey],
                        'price error',
                    )
                    return {
                        icon: 'checked',
                        tip: $t('cart.actions.price_confirm', {
                            currency: $s.identity.user.currency,
                            new_price: format_money_with_symbol(
                                unit_price(price_error.valid_value, offer_item.case_number_of_bottles),
                                offer_item.currency,
                            ),
                            old_price: format_money_with_symbol(
                                unit_price(price_error.invalid_value, offer_item.case_number_of_bottles),
                                offer_item.currency,
                            ),
                            product: offer_item.product_name,
                            unit: $t(`unit.${$s.identity.user.price_preference}`, {count: 1}),
                        }),
                        type: 'warning',
                    }
                }

                if ((data._model_value ?? 0) > data._cart_units) {
                    let tip
                    if (data._model_value_invalid) {
                        // The intended amount is invalid; always use the same tip.
                        tip = $t('cart.actions.plus_disabled', {product: offer_item.product_name})
                    } else {
                        tip = $t('cart.actions.plus', {
                            count: (data._model_value ?? 0) - data._cart_units,
                            product: offer_item.product_name,
                            units: $t(`unit.${$s.identity.user.price_preference}`, {
                                count: (data._model_value ?? 0) - data._cart_units,
                            }),
                        })
                    }

                    return {
                        icon: 'checked',
                        tip,
                        type: data._cart_invalid ? 'warning' : 'info',
                    }
                } else if (data._model_value === data._cart_units) {
                    let tip
                    const next_valid_unit = context.find_next_valid_units(data._cart_units)
                    if (next_valid_unit > data._cart_units) {
                        tip = $t('cart.actions.plus', {
                            count: next_valid_unit - data._cart_units,
                            product: offer_item.product_name,
                            units: $t(`unit.${$s.identity.user.price_preference}`, {
                                count: next_valid_unit - data._cart_units,
                            }),
                        })
                    } else {
                        tip = $t('cart.actions.plus_disabled', {product: offer_item.product_name})
                    }
                    return {
                        icon: 'plus',
                        tip,
                        type: 'info',
                    }
                } else if ((data._model_value ?? 0) < data._cart_units) {
                    return {
                        icon: 'plus',
                        tip: $t('cart.actions.plus_disabled', {product: offer_item.product_name}),
                        type: 'info',
                    }
                }
                return undefined
            },
        },
    })

    const context = {
        data,

        /**
         * Reset the model value override so _model_value falls back to _cart_units.
         * Call this after cart operations complete to ensure the input
         * reflects the committed cart value.
         */
        sync_model_value() {
            data._model_value_override = undefined
        },

        /**
         * Find the next valid quantity from current position,
         * respecting MOQ, step_size, and available stock.
         */
        find_next_valid_units(current_units: number): number {
            const stock_units = data._stock_units

            if (stock_units === null) {
                let max_quantity = DEFAULT_MAX_QUANTITY
                const next_step_units = Math.max(current_units + data._step_size_unit, data._min_quantity_unit)

                if ($s.cart.show_bottles) {
                    max_quantity *= offer_item.case_number_of_bottles
                }

                return Math.min(next_step_units, max_quantity) // No stock limit; just step forward until max quantity
            }

            // If we're already at maximum available, can't go higher
            if (current_units >= stock_units) {
                return current_units
            }

            let next_valid_units

            if (current_units === 0) {
                // From 0, go to MOQ (or step_size if no MOQ)
                next_valid_units = data._min_quantity_unit > 0 ? data._min_quantity_unit : data._step_size_unit
            } else if (current_units < data._min_quantity_unit) {
                // Below MOQ, go to MOQ
                next_valid_units = data._min_quantity_unit
            } else {
                // Above or at MOQ: find next multiple of step_size
                const next_multiple = Math.ceil((current_units + 1) / data._step_size_unit) * data._step_size_unit
                next_valid_units = next_multiple
            }

            // Cap at available stock
            if (next_valid_units > stock_units) {
                next_valid_units = stock_units
            }

            // Additional safety: if we can't actually move forward, return current
            if (next_valid_units <= current_units) {
                return current_units
            }

            return next_valid_units
        },

        /**
         * Find the previous valid quantity from current position,
         * respecting MOQ and step_size.
         */
        find_prev_valid_units(current_units: number): number {
            const min_qty = data._min_quantity_unit
            const step_size = data._step_size_unit

            let prev_valid_units

            if (current_units === 0) {
                prev_valid_units = 0
            } else if (current_units <= min_qty) {
                prev_valid_units = 0
            } else {
                // Above MOQ: find largest multiple of step_size that is < current
                const largest_multiple = Math.floor((current_units - 1) / step_size) * step_size

                if (largest_multiple >= min_qty) {
                    prev_valid_units = largest_multiple
                } else if (current_units > min_qty) {
                    prev_valid_units = min_qty
                } else {
                    prev_valid_units = 0
                }
            }

            return prev_valid_units
        },
    }

    return context
}

export type QuantityContext = ReturnType<typeof create_quantity_context>

export const quantity_registry = new Registry(create_quantity_context)
